import { Block, CellSpec, dayHeading, INK, PAGE, PdfDoc, reportDate, TableSpec, text, TextStyle } from "./pdfDoc";

/**
 * The daily report as the team writes it: App Daily Report 28Oct2026.pdf,
 * reproduced. Every position, size and colour below was measured from that
 * file (points on a Letter page), so a change here is a change to how the
 * team's report looks.
 */

export type DailySection = "CALL" | "LEVELS" | "GROWTH" | "ACTIVITY";

export interface DailyRow {
  metric: string;
  section: DailySection;
  label: string;
  hint: string;
  source: "APP" | "MANUAL";
  target: number | null;
  values: Array<number | null>;
  total: number | null;
  totalSoFar: number | null;
}

export interface DailyReportData {
  days: string[];
  rows: DailyRow[];
  levelsRecordedSince: string | null;
  chatMessagesSource: "GETSTREAM" | "APP";
  phoneVerifiedDatesExactSince: string;
}

export interface DailyPdfOptions {
  preparedBy: string;
  /** The bullet under each section heading; blank leaves the bullet out. */
  notes: Partial<Record<DailySection, string>>;
  /** Rows the team's report does not have: lead generation, live rooms, the weekly call. */
  includeExtras: boolean;
  /** A last section saying what each number counts and where it is incomplete. */
  includeMeanings: boolean;
}

/** The section headings, as the team's report writes them. */
export const DAILY_PDF_HEADINGS: Record<DailySection, string> = {
  LEVELS: "Membership activity levels",
  GROWTH: "Leadgen - WhatsApp - App funnel",
  ACTIVITY: "App activity",
  CALL: "Weekly call",
};

/**
 * The template's rows, in its order, plus Referrals, which the team asked for after the 28 Oct
 * report, and less New accounts, which they asked to drop from the report on 4 Nov.
 */
const FUNNEL = ["users_name_email", "members_name_phone_email", "referrals", "ios_downloads", "android_downloads"];
const ACTIVITY = ["opened_app", "shared_link", "recommended", "chat_messages"];
const EXTRA_FUNNEL = ["leadgen_members"];
const EXTRA_ACTIVITY = ["live_room"];

const NOT_AVAILABLE = "Not yet Available";
const MARGIN = 72;
/** The furthest right a table may reach; the template's funnel table ends at 588. */
const RIGHT_EDGE = { portrait: 590, landscape: 760 };

// No thousands separator: the team writes 11200.
const number = (value: number | null | undefined) => (value === null || value === undefined ? "" : String(value));

/** Fits the day columns between the fixed ones, never wider than the template's. */
function dayWidths(days: number, fixed: number, x: number, right: number, widest: number): number[] {
  const each = Math.min(widest, Math.floor(((right - x - fixed) / Math.max(days, 1)) * 10) / 10);
  return Array.from({ length: days }, () => each);
}

export function buildDailyReportPdf(report: DailyReportData, options: DailyPdfOptions): PdfDoc {
  const { days } = report;
  const lastDay = days[days.length - 1];
  // Up to a week fits the template's portrait page; more days turn it sideways.
  const orientation = days.length > 7 ? "landscape" : "portrait";
  const right = RIGHT_EDGE[orientation];
  const wide = orientation === "landscape";
  const byMetric = new Map(report.rows.map((row) => [row.metric, row]));
  const pick = (keys: string[]) => keys.map((key) => byMetric.get(key)).filter((row): row is DailyRow => !!row);
  const has = (row: DailyRow) => row.values.some((value) => value !== null && value !== 0);
  const blocks: Block[] = [];
  // The bullet's text starts at 144 and wraps at the right margin.
  const bulletWidth = PAGE[orientation].width - MARGIN - 144;

  // ---- Title: Arial Bold 11, three lines 14.5pt apart -----------------------
  const title = text("helvetica", "bold", 11, INK.black);
  const titleLines = ["I Love Black People App Daily Report", reportDate(lastDay)];
  if (options.preparedBy.trim()) titleLines.push(`Prepared by: ${options.preparedBy.trim()}`);
  titleLines.forEach((line, index) =>
    blocks.push({ kind: "text", text: line, style: title, x: MARGIN, gapBefore: index === 0 ? 0 : 0.5, lineHeight: 14, maxWidth: 468 }),
  );

  const heading = (section: DailySection, style: TextStyle, gapBefore: number) =>
    blocks.push({ kind: "text", text: DAILY_PDF_HEADINGS[section], style, x: MARGIN, gapBefore, lineHeight: style.size + 3, maxWidth: 468 });
  /** The bullet under a heading; returns the gap the table after it needs. */
  const note = (section: DailySection, style: TextStyle, lineHeight: number, gapBefore: number, tableGap: number) => {
    const written = (options.notes[section] ?? "").trim();
    if (!written) return 18;
    blocks.push({ kind: "bullet", text: written, style, bulletX: 126, bulletSize: 12, x: 144, gapBefore, lineHeight, maxWidth: bulletWidth });
    return tableGap;
  };

  // ---- Membership activity levels -------------------------------------------
  // Times Bold 12 #4f81bd; an Arial 12 bullet; a black-ruled table, every cell
  // Times Bold 11: targets and day headings blue, Beginner's days blue.
  heading("LEVELS", text("times", "bold", 12, INK.blue), 17);
  let gap = note("LEVELS", text("helvetica", "normal", 12, INK.black), 16, 17, 15);
  const levels = report.rows.filter((row) => row.section === "LEVELS");
  const levelsX = MARGIN;
  const levelsWidths = [58, 44, ...dayWidths(days.length, 58 + 44 + 52, levelsX, right, 52), 52];
  const levelsText = text("times", "bold", wide ? 9 : 11, INK.black);
  const blueBold: Partial<TextStyle> = { color: INK.blue };
  blocks.push({
    kind: "table",
    gapBefore: gap,
    table: {
      x: levelsX,
      widths: levelsWidths,
      text: levelsText,
      headText: {},
      lineColor: INK.black,
      lineWidth: 1,
      padding: { top: 7, right: 3, bottom: 4, left: 5 },
      headHeight: 36,
      rowHeight: 24,
      valign: "top",
      showHead: "firstPage",
      head: [
        [
          { text: "" },
          { text: "Daily KPI" },
          ...days.map((day) => ({ text: dayHeading(day), style: blueBold })),
          { text: "Totals so far", style: blueBold },
        ],
      ],
      body: levels.map((row) => [
        { text: row.label },
        { text: number(row.target), style: blueBold },
        ...row.values.map((value) => ({ text: number(value), style: row.label === "Beginner" ? blueBold : undefined })),
        { text: number(row.totalSoFar) },
      ]),
    },
  });

  // ---- Leadgen - WhatsApp - App funnel --------------------------------------
  // Times Bold 13 #4a86e8; a Times 13 bullet; a table with a #4f81bd header in
  // white Times Bold 11 and #95b3d7 rules, grey labels and blue bold numbers.
  heading("GROWTH", text("times", "bold", 13, INK.brightBlue), 42);
  gap = note("GROWTH", text("times", "normal", 13, INK.black), 17, 15, 41);
  const funnel = [...pick(FUNNEL), ...(options.includeExtras ? pick(EXTRA_FUNNEL).filter((row) => row.total !== null) : [])];
  blocks.push({ kind: "table", gapBefore: gap, table: funnelTable(funnel, days, right, wide, "Total weekly") });

  // ---- App activity -----------------------------------------------------------
  // Arial Bold 11.5 #4a86e8; the funnel's table without a target or total.
  heading("ACTIVITY", text("helvetica", "bold", 11.5, INK.brightBlue), 48);
  gap = note("ACTIVITY", text("helvetica", "normal", 12, INK.black), 16, 15, 34);
  const activity = [...pick(ACTIVITY), ...(options.includeExtras ? pick(EXTRA_ACTIVITY) : [])];
  blocks.push({ kind: "table", gapBefore: gap === 18 ? 34 : gap, table: activityTable(activity, days, right, wide) });

  // ---- Weekly call: not in the template; only when someone typed numbers in ---
  const call = report.rows.filter((row) => row.section === "CALL");
  if (options.includeExtras && call.some(has)) {
    heading("CALL", text("times", "bold", 13, INK.brightBlue), 42);
    gap = note("CALL", text("times", "normal", 13, INK.black), 17, 15, 34);
    blocks.push({ kind: "table", gapBefore: gap === 18 ? 34 : gap, table: funnelTable(call.filter(has), days, right, wide, "Total") });
  }

  if (options.includeMeanings) blocks.push(...meanings(report, [...funnel, ...activity]));

  return {
    fileName: `ILBP Daily Report ${lastDay}.pdf`,
    orientation,
    margin: { top: MARGIN, right: MARGIN, bottom: MARGIN, left: MARGIN },
    blocks,
    footer: null,
  };
}

/**
 * A day's value in the blue tables: blue bold 13, or "Not yet Available" in
 * magenta for a typed-in row nobody filled, at 10pt as the template has it, or
 * smaller when a week's columns are too narrow for "Available" on one line.
 */
function blueValue(value: number | null, row: DailyRow, size: number, notAvailableSize: number): CellSpec {
  if (value === null && row.source === "MANUAL") {
    return { text: NOT_AVAILABLE, style: { size: notAvailableSize, color: INK.magenta, style: "bold" } };
  }
  return { text: number(value), style: { size, color: INK.blue, style: "bold" } };
}

function funnelTable(rows: DailyRow[], days: string[], right: number, wide: boolean, totalHeading: string): TableSpec {
  const x = 70;
  const valueSize = wide ? 10 : 13;
  const dayColumns = dayWidths(days.length, 116 + 48 + 56, x, right, 50);
  // "Available" in Times Bold is 4pt wide per point of size, plus the 7pt of padding.
  const notAvailableSize = Math.min(10, Math.floor(((dayColumns[0] ?? 50) - 8) / 4));
  return {
    x,
    widths: [116, 48, ...dayColumns, 56],
    text: text("times", "normal", wide ? 9 : 11, INK.grey),
    headText: { style: "bold", color: INK.white },
    headFill: INK.blue,
    lineColor: INK.rule,
    lineWidth: 1,
    padding: { top: 2, right: 3, bottom: 4, left: wide ? 6 : 16 },
    headHeight: 39,
    rowHeight: 40,
    valign: "top",
    showHead: "firstPage",
    head: [
      [
        { text: "Metric" },
        { text: "Daily Target KPI", style: { size: 8 } },
        ...days.map((day) => ({ text: dayHeading(day) })),
        { text: totalHeading },
      ],
    ],
    body: rows.map((row) => [
      { text: row.label, padding: { left: 17 } },
      { text: number(row.target), style: { style: "bold", color: INK.darkBlue } },
      ...row.values.map((value) => ({ ...blueValue(value, row, valueSize, notAvailableSize), padding: { top: 7, left: 4 } })),
      // A total nobody could add up stays blank, as the template leaves it.
      row.total === null ? { text: "" } : { ...blueValue(row.total, row, valueSize, notAvailableSize), padding: { top: 7, left: 4 } },
    ]),
  };
}

function activityTable(rows: DailyRow[], days: string[], right: number, wide: boolean): TableSpec {
  const x = 70;
  return {
    x,
    widths: [118, ...dayWidths(days.length, 118, x, right, 48)],
    text: text("times", "normal", wide ? 9 : 11, INK.darkGrey),
    headText: { style: "bold", color: INK.white },
    headFill: INK.blue,
    lineColor: INK.rule,
    lineWidth: 1,
    padding: { top: 2, right: 3, bottom: 4, left: 5 },
    headHeight: 30,
    rowHeight: 30,
    valign: "top",
    showHead: "firstPage",
    head: [[{ text: "Date" }, ...days.map((day) => ({ text: dayHeading(day), padding: { left: wide ? 4 : 17 } }))]],
    body: rows.map((row) => [
      { text: row.label },
      ...row.values.map((value) => ({ text: number(value), style: { style: "bold" as const, color: INK.blue }, padding: { top: 7, left: 6 } })),
    ]),
  };
}

/**
 * What each row counts, and where a number is known to be incomplete. Not in
 * the team's report: it comes after everything that is, so the template's
 * pages are unchanged.
 */
function meanings(report: DailyReportData, rows: DailyRow[]): Block[] {
  const small = text("helvetica", "normal", 9, INK.muted);
  const item = (line: string, gapBefore = 3): Block => ({ kind: "text", text: line, style: small, x: MARGIN, gapBefore, lineHeight: 11.5, maxWidth: 468 });
  const first = report.days[0];
  const caveats: string[] = [];
  if (report.chatMessagesSource === "APP") {
    caveats.push(
      "Chat messages come from the app's own log because getstream could not be read. The log misses messages sent from older app builds, so the count is low.",
    );
  }
  if (first < report.phoneVerifiedDatesExactSince) {
    caveats.push(
      `Members (name, phone number, email) before ${dayHeading(report.phoneVerifiedDatesExactSince)}: the day a phone number was verified was not recorded then, so a member is counted on the day they registered.`,
    );
  }
  if (!report.levelsRecordedSince || first < report.levelsRecordedSince) {
    caveats.push(
      `Level-ups are recorded from ${report.levelsRecordedSince ? dayHeading(report.levelsRecordedSince) : "the first one after the reports update"}; days before that show 0 because nothing was recorded.`,
    );
  }
  return [
    {
      kind: "text",
      text: "What the numbers mean",
      style: text("helvetica", "bold", 11.5, INK.brightBlue),
      x: MARGIN,
      gapBefore: 36,
      lineHeight: 15,
      maxWidth: 468,
    },
    item("Levels: members who reached each level that day. Totals so far: members on that level now. Beginner: that day's new accounts.", 4),
    ...rows.map((row) => item(`${row.label}: ${row.hint}.`)),
    item(
      "Downloads and lead generation are typed in by hand; Not yet Available means nobody has entered that day yet. Days are counted in the timezone of the dashboard that exported this report.",
    ),
    ...caveats.map((line, index) => item(line, index === 0 ? 8 : 3)),
  ];
}
